import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CIcon from "@coreui/icons-react";
import { cilBadge } from "@coreui/icons";
import { useMyMemberships } from "../../../api/hooks/useMemberships";
import { isExternalTier, useMembershipTiers } from "../../../api/hooks/useMembershipTiers";
import { useTradingStrategies } from "../../../api/hooks/useTradingStrategies";
import { routePaths } from "../../../routes/route-paths";
import { countStrategiesByIds } from "../strategySides";

/**
 * Points someone looking at strategies one at a time at the membership that gets them the most at once. Gone
 * once they hold any membership.
 */
export function MembershipBanner() {
  const { t } = useTranslation("tradingStrategies");
  const tiers = useMembershipTiers();
  const memberships = useMyMemberships();
  const catalogue = useTradingStrategies().data ?? [];

  // Nothing until both have loaded, so it never flashes up for someone who already holds one.
  if (!tiers.data || !memberships.data || memberships.data.length > 0) {
    return null;
  }

  // Counted as strategies, not sides, as the Strategies list shows them.
  const counted = tiers.data
    .filter(isExternalTier)
    .map((tier) => ({ tier, count: countStrategiesByIds(tier.tradingStrategyIds, catalogue) }));
  const biggest = counted.reduce<(typeof counted)[number] | null>(
    (best, candidate) => (best === null || candidate.count > best.count ? candidate : best),
    null,
  );
  if (biggest === null || biggest.count === 0) {
    return null;
  }

  return (
    <div className="tinted-info d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-4 p-3 rounded">
      <div>
        <div className="fw-semibold">
          {t("membershipBanner.title", { count: biggest.count })}
        </div>
        <div className="small text-body-secondary">
          {t("membershipBanner.body", { tier: biggest.tier.name, platform: biggest.tier.platform.name })}
        </div>
      </div>
      <Link to={routePaths.memberships} className="btn btn-primary flex-shrink-0">
        <CIcon icon={cilBadge} className="me-2" />
        {t("membershipBanner.action")}
      </Link>
    </div>
  );
}
